import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import "../styles/Dashboard.scss";
import SvgIcn from "../data/IconCompo";
import DeleteUserPopup from "../ui/compoP/DeleteUserPopup";
import DeleteItemPopup from "../ui/compoP/DeleteItemPopup";
import AddItemDashboardPopup from "../ui/compoP/AddItemDashboardPopup";
import { useDispatch } from "react-redux";
import { validationPopup } from "../redux/actions";

// Import services
import {
  getAllItems,
  getAllUsers,
  uploadManyImages,
  addManyItems,
  deleteUser,
  deleteItem,
} from "../services/AdminService";

// Import utility functions
import {
  getFilteredUsers,
  getFilteredItems,
  handleDeleteUser,
  handleDeleteItem,
  handleAddItem,
} from "../utils/dashboardUtils";

// Import components
import DashboardStats from "../components/dashboard/DashboardStats";
import DashboardControls from "../components/dashboard/DashboardControls";
import UsersTable from "../components/dashboard/UsersTable";
import ItemsTable from "../components/dashboard/ItemsTable";
import AllPopups from "../ui/compoP/AllPopups";
import { getUserInfo } from "../services/UserService";

function Dashboard() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check authentication and admin status on component mount
  useEffect(() => {
    const checkAuthAndAdmin = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        // Get the username from the decoded token
        const decodedToken = jwtDecode(token);
        const userResponse = await getUserInfo(decodedToken.username);

        const isUserAdmin =
          userResponse.data.myUser.role.name === "admin" &&
          userResponse.data.myUser.role.status === "accepted";

        if (!isUserAdmin) {
          navigate("/");
          return;
        }

        setIsAdmin(true);
        setLoading(false);
      } catch (error) {
        console.error("Error checking admin status:", error);
        navigate("/");
      }
    };

    checkAuthAndAdmin();
  }, [navigate]);

  // If still loading or not admin, show loading spinner

  // If not admin, don't render anything (though this shouldn't happen due to navigation)

  // State variables
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [imgError, setImgError] = useState([]);
  const [activeView, setActiveView] = useState(() => {
    // Initialize activeView from localStorage or default to "users"
    return localStorage.getItem("dashboardActiveView") || "users";
  });
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [userToDelete, setUserToDelete] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  // Refs
  const multipleImagesRef = useRef(null);
  const jsonFileRef = useRef(null);

  const dispatch = useDispatch();

  // Save activeView to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("dashboardActiveView", activeView);
  }, [activeView]);

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersResponse, itemsResponse] = await Promise.all([
          getAllUsers(),
          getAllItems(),
        ]);

        setUsers(usersResponse.data || []);
        setItems(itemsResponse.data?.items || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Event handlers
  const handleMessageClick = (username) => {
    console.log(`Message clicked for ${username}`);
  };

  const handleDeleteUserClick = (user) => {
    setUserToDelete(user);
  };

  const handleDeleteItemClick = (item) => {
    setItemToDelete(item);
  };

  const handleEditItemClick = (item) => {
    setItemToEdit(item);
    setShowAddItemModal(true);
  };
  const getCategoryCounts = () => {
    console.log(items);
    const counts = {
      ALL: items?.length || 0,
      tools:
        items?.filter((item) => item.category_id[0].name === "tools").length ||
        0,
      resos:
        items?.filter((item) => item.category_id[0].name === "resos").length ||
        0,
      ideaz:
        items?.filter((item) => item.category_id[0].name === "ideaz").length ||
        0,
      apps:
        items?.filter((item) => item.category_id[0].name === "apps").length ||
        0,
    };
    return counts;
  };

  const confirmDeleteUser = async () => {
    const success = await handleDeleteUser(
      userToDelete,
      users,
      setUsers,
      dispatch
    );
    if (success) {
      setUserToDelete(null);
    }
  };

  const confirmDeleteItem = async () => {
    const success = await handleDeleteItem(
      itemToDelete,
      items,
      setItems,
      dispatch
    );
    if (success) {
      setItemToDelete(null);
    }
  };

  const handleUploadImages = async (e) => {
    try {
      const files = e.target.files;
      if (!files.length) return;

      setLoading(true);
      const result = await uploadManyImages(files);

      // Check results
      const failures = result.filter((r) => r.status === "rejected");
      if (failures.length) {
        console.error("Some images failed to upload:", failures);
      }

      // Refresh items list
      const itemsResponse = await getAllItems();
      setItems(itemsResponse.data?.items || []);
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddManyItems = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;

      setLoading(true);
      await addManyItems(file);

      // Refresh items list
      const itemsResponse = await getAllItems();
      setItems(itemsResponse.data?.items || []);
    } catch (error) {
      console.error("Error adding items:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (target) => {
    setSearchTerm(target.value);
  };

  const handleDropdownSelect = (selectedValue) => {
    if (activeView === "users") {
      setRoleFilter(selectedValue || "ALL");
    } else {
      setCategoryFilter(selectedValue.value || "ALL");
    }
  };

  const handleSaveNewItem = async (itemData, image) => {
    try {
      await handleAddItem(itemData, image, dispatch);

      // Refresh items list
      const itemsResponse = await getAllItems();
      setItems(itemsResponse.data?.items || []);
      setShowAddItemModal(false);
      setItemToEdit(null);
    } catch (error) {
      console.error("Error saving new item:", error);
    }
  };

  const handleCloseItemModal = () => {
    setShowAddItemModal(false);
    setItemToEdit(null);
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const getSortedItems = (items) => {
    if (!sortConfig.key) return items;

    return [...items].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle special cases
      if (sortConfig.key === "category") {
        // Safely handle potentially undefined category_id
        aValue = (a.category_id && a.category_id[0]?.name) || "";
        bValue = (b.category_id && b.category_id[0]?.name) || "";
      } else if (sortConfig.key === "platform" || sortConfig.key === "tags") {
        aValue = (Array.isArray(aValue) ? aValue.join(",") : "").toLowerCase();
        bValue = (Array.isArray(bValue) ? bValue.join(",") : "").toLowerCase();
      } else if (typeof aValue === "string") {
        aValue = (aValue || "").toLowerCase();
        bValue = (bValue || "").toLowerCase();
      } else {
        // Handle numeric values or undefined
        aValue = aValue || 0;
        bValue = bValue || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  };

  // Filter data based on search and filters
  const filteredData =
    activeView === "users"
      ? getFilteredUsers(users, searchTerm.toLowerCase(), roleFilter)
      : getFilteredItems(items, searchTerm.toLowerCase(), categoryFilter);

  // Apply sorting to filteredData before rendering
  const sortedItems = getSortedItems(filteredData);

  // Loading state
  if (loading) {
    return (
      <div className="loading-spinner-box">
        <SvgIcn name="loading" />
        <p>Loading...</p>
      </div>
    );
  }

  return loading ? (
    <div className="loading-spinner-box">
      <SvgIcn name="loading" />
      <p>Loading...</p>
    </div>
  ) : (
    isAdmin && (
      <div className="dashboard-container">
        <div className="users-list">
          <div className="users-header">
            <div className="dashboard-stats">
              {/* Stats section */}
              <DashboardStats
                users={users}
                items={items}
                activeView={activeView}
                setActiveView={setActiveView}
              />

              {/* Controls section */}
              <DashboardControls
                activeView={activeView}
                multipleImagesRef={multipleImagesRef}
                jsonFileRef={jsonFileRef}
                setShowAddItemModal={setShowAddItemModal}
                handleSearch={handleSearch}
                handleDropdownSelect={handleDropdownSelect}
                searchTerm={searchTerm}
                roleFilter={roleFilter}
                categoryFilter={categoryFilter}
                filteredDataLength={filteredData.length}
                handleAddManyItems={handleAddManyItems}
                handleUploadImages={handleUploadImages}
                counts={getCategoryCounts()}
              />
            </div>

            {/* Table headers and content */}
            {activeView === "users" ? (
              <UsersTable
                users={filteredData}
                handleMessageClick={handleMessageClick}
                handleDeleteUserClick={handleDeleteUserClick}
              />
            ) : (
              <ItemsTable
                items={sortedItems}
                handleDeleteItemClick={handleDeleteItemClick}
                handleEditItemClick={handleEditItemClick}
                imgError={imgError}
                setImgError={setImgError}
                onSort={handleSort}
              />
            )}
          </div>
        </div>

        {/* Modals */}
        {showAddItemModal && (
          <AddItemDashboardPopup
            onClose={handleCloseItemModal}
            onSave={handleSaveNewItem}
            itemToEdit={itemToEdit}
          />
        )}

        {userToDelete && (
          <DeleteUserPopup
            user={userToDelete}
            onClose={() => setUserToDelete(null)}
            onConfirm={confirmDeleteUser}
          />
        )}

        {itemToDelete && (
          <DeleteItemPopup
            item={itemToDelete}
            onClose={() => setItemToDelete(null)}
            onConfirm={confirmDeleteItem}
          />
        )}
        <AllPopups />
      </div>
    )
  );
}

export default Dashboard;
